import { useTranslation } from "react-i18next";
import {
  facilityTypes,
  workingHours,
  shiftModels,
  contractTypes,
  benefits,
  requirements,
  positions
} from "../data";

/**
 * Hook to get translated configuration values
 * This allows for easy translation of configuration keys to localized labels
 */
export function useConfigTranslation() {
  const { t } = useTranslation(["config"]);

  const getFacilityTypeLabel = (value: string): string => {
    const config = facilityTypes.find((type) => type.value === value);
    if (config) {
      return t(`facility_types.${value}`, config.label);
    }
    return value;
  };

  const getWorkingHoursLabel = (value: string): string => {
    const config = workingHours.find((wh) => wh.value === value);
    if (config) {
      return t(`working_hours.${value}`, config.label);
    }
    return value;
  };

  const getShiftModelLabel = (value: string): string => {
    const config = shiftModels.find((sm) => sm.value === value);
    if (config) {
      return t(`shift_models.${value}`, config.label);
    }
    return value;
  };

  const getContractTypeLabel = (value: string): string => {
    const config = contractTypes.find((ct) => ct.value === value);
    if (config) {
      return t(`contract_types.${value}`, config.label);
    }
    return value;
  };

  const getBenefitLabel = (value: string): string => {
    const config = benefits.find((b) => b.value === value);
    if (config) {
      return t(`benefits.${value}`, config.label);
    }
    return value;
  };

  const getRequirementLabel = (value: string): string => {
    const config = requirements.find((r) => r.value === value);
    if (config) {
      return t(`requirements.${value}`, config.label);
    }
    return value;
  };

  const getPositionLabel = (value: string): string => {
    const config = positions.find((p) => p.value === value);
    if (config) {
      return t(`positions.${value}`, config.label);
    }
    return value;
  };

  return {
    getFacilityTypeLabel,
    getWorkingHoursLabel,
    getShiftModelLabel,
    getContractTypeLabel,
    getBenefitLabel,
    getRequirementLabel,
    getPositionLabel
  };
}

/**
 * Utility functions for non-hook usage (e.g., in data processing)
 */
export const getFacilityTypeLabelStatic = (value: string): string => {
  const config = facilityTypes.find((type) => type.value === value);
  return config ? config.label : value;
};

export const getWorkingHoursLabelStatic = (value: string): string => {
  const config = workingHours.find((wh) => wh.value === value);
  return config ? config.label : value;
};

export const getContractTypeLabelStatic = (value: string): string => {
  const config = contractTypes.find((ct) => ct.value === value);
  return config ? config.label : value;
};

export const getBenefitLabelStatic = (value: string): string => {
  const config = benefits.find((b) => b.value === value);
  return config ? config.label : value;
};

export const getRequirementLabelStatic = (value: string): string => {
  const config = requirements.find((r) => r.value === value);
  return config ? config.label : value;
};

export const getPositionLabelStatic = (value: string): string => {
  const config = positions.find((p) => p.value === value);
  return config ? config.label : value;
};
